import React, {useId} from 'react';
import Plotly, {LayoutAxis} from "plotly.js"
import {AxesConfig, DatasetChartProps, PlotDataEx} from "./interfaces";
import {clipboardIcon} from "./icons";
import Plot from "react-plotly.js";
import ChartWrapper from "./ChartWrapper/ChartWrapper";
import {DatasetDataInfoResponse} from "api/apiModels";
import {copyChartToClipBoard} from "./chartUtils";
import {APIResponse} from "../../../services/HttpService";

const commonAxisConfig = {
    showspikes: true,
    spikemode: "toaxis",
    automargin: true,
} as Partial<LayoutAxis>

const xAxisCommonConfig = {
    spikemode: "toaxis",
    showspikes: true
}

const layoutConfig = {
    legend: {
        "orientation": "h",
        y: -0.20
    },
    margin: {
        l: 0,
        r: 0,
        b: 30,
        t: 20,
    }
} as Plotly.Layout

export default function HistogramChart(props: DatasetChartProps) {
    const uniqueId = useId();
    const axesConfig = (data: Array<Partial<PlotDataEx>>) => {
        const axes: AxesConfig = {}
        const domain = [0, 1]
        if (data.length > 0) {
            const unit = data[0]?.unit || ""

            axes.xaxis = {
                ...xAxisCommonConfig,
                domain: domain,
                title: {
                    text: unit,
                    standoff: 10
                },
            } as LayoutAxis

            axes.yaxis = {
                title: {
                    text: "%",
                    standoff: 10
                },
                ...commonAxisConfig
            } as LayoutAxis
        }

        return axes
    }

    const chartConfig: Partial<Plotly.Config> = {
        displaylogo: false,
        modeBarButtonsToRemove: ["lasso2d", "select2d"],
        modeBarButtonsToAdd: [
            {
                title: 'Copy chart to Clipboard',
                name: 'Copy chart to Clipboard',
                icon: clipboardIcon,
                click: copyChartToClipBoard
            }
        ],
    }

    const promiseHandler = (res: APIResponse<any>) => {
        return res.data.map((ds: DatasetDataInfoResponse) => {
            return mapQueryData(ds)
        });
    }

    const mapQueryData = (ds: DatasetDataInfoResponse) => {
        const x: number[] = []
        const custom: number[] = []
        ds.data.forEach((v) => {
            x.push(v.value)
        });

        if (props.bins && props.bins.size && props.bins.end && props.bins.start) {
            const sortedNumbers = [...x].sort((a, b) => a - b);
            if (sortedNumbers.length > 0) {
                let currVal = Math.floor(props.bins.start as number);

                while (currVal < Math.ceil(props.bins.end as number)) {
                    let count = 0;
                    for (let i = 0; i < sortedNumbers.length; i++) {
                        if (sortedNumbers[i] >= currVal && sortedNumbers[i] < (currVal + (props.bins.size as number))) {
                            count++;
                        }
                    }
                    custom.push(count);
                    currVal += props.bins.size as number;
                }
            }
        }

        return {
            name: ds.title,
            type: "histogram",
            unit: ds.unit,
            showlegend: true,
            hovertemplate: `<b>%{y:.2f}%</b> at %{x} <br>${ds.title}<br>${props.bins && props.bins.end && props.bins.start && props.bins.size ? `Count: %{customdata}` : ""}<extra></extra>`,
            x: x,
            histnorm: "percent",
            xbins: props.bins,
            customdata: custom
        } as unknown as PlotDataEx
    }

    return <ChartWrapper uniqueKey={uniqueId}
                         identifiers={props.identifiers}
                         showDateSelection={true}
                         promiseHandler={promiseHandler}
                         axesConfig={axesConfig}
                         updateFrequency={props.updateFrequency}
                         height={props.height}
                         loadConf={props.loadConf}
                         hideRefresh={props.hideRefresh}
                         dynamicHeight={props.dynamicHeight}
                         referenceTime={props.referenceTime}
    >
        {(props) => (
            <Plot className="ea-plotly-chart"
                  config={{...chartConfig}}
                  data={[...props.data]}
                  layout={{
                      ...props.axesConfig,
                      ...layoutConfig,
                      width: props.width,
                      height: props.height,
                      bargap: 0.2,
                  }}
            />
        )}
    </ChartWrapper>
}